/*
 * Ryan O'Dowd
 * 2024-03-01
 * © Copyright 2024 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import React, {
  useState,
} from 'react';
import {
  createModule,
  deleteModule,
  updateModule,
} from '@utils/store/actions';
import Button from '@components/common/Button';
import DateRange from '../DateRange';
import Dialog from '@components/common/Dialog';
import FormControl from '@components/common/FormControl';
import TextInput from '@components/common/TextInput';
import {
  View,
} from 'react-native';
import getStyles from './styles';
import hooks from '@hooks';

const EditModuleDialog = (props: EditModuleDialogProps) => {
  const styles = hooks.useStyles(getStyles);
  const i18n = hooks.useI18n();
  const dispatch = hooks.useTypedDispatch();

  const [_moduleName, setModuleName] = useState(props.isNew ? '' : props.module.name);
  // @TODO: const [_status, setStatus] = useState(props.module?.status || 'draft');

  const [_dateAvailable, setDateAvailable] = useState(props.module?.date_available ? new Date(props.module.date_available) : undefined);
  const [_dateDeadline, setDateDeadline] = useState(props.module?.date_deadline ? new Date(props.module.date_deadline) : undefined);

  const [_pipeDelimitedText, setPipeDelimitedText] = useState('');

  const getMinDateAvailable = () => { // @TODO: abstract with EditModuleDialog
    const goalDateAvailable = props.goal.date_available ? new Date(props.goal.date_available) : undefined;

    return goalDateAvailable;
  };

  const getMaxDateAvailable = () => { // @TODO: abstract with EditModuleDialog
    const goalDateDeadline = props.goal.date_deadline ? new Date(props.goal.date_deadline) : undefined;
    const moduleDateDeadline = _dateDeadline;

    return new Date(Math.max(...[goalDateDeadline?.getTime(), moduleDateDeadline?.getTime()].filter(Boolean)));
  };

  const getMinDateDeadline = () => { // @TODO: abstract with EditAssignmentDialog
    const moduleDateAvailable = _dateAvailable;
    const goalDateAvailable = props.goal.date_available ? new Date(props.goal.date_available) : undefined;

    return new Date(Math.max(...[moduleDateAvailable?.getTime(), goalDateAvailable?.getTime()].filter(Boolean)));
  };

  const getMaxDateDeadline = () => { // @TODO: abstract with EditModuleDialog
    const goalDateDeadline = props.goal.date_deadline ? new Date(props.goal.date_deadline) : undefined;

    return goalDateDeadline;
  };

  return (
    <Dialog
      visible={true}
      onDismiss={() => props.onClose()} // @TODO: ask about unsaved changes
    >
      <Dialog.Title>
        {props.isNew ? 'Create new module' : 'Edit module'}
      </Dialog.Title>{/* @TODO: i18n */}
      <Dialog.Content>
        <TextInput
          autoFocus={true}
          value={_moduleName}
          mode='outlined'
          onChangeText={(text) => setModuleName(text)}
          label='Module name'
          placeholder='e.g. "Jesus Feeds the 5000"'
        />

        <DateRange
          dateAvailable={_dateAvailable}
          dateDeadline={_dateDeadline}
          setDateAvailable={(newValue) => setDateAvailable(newValue)}
          setDateDeadline={(newValue) => setDateDeadline(newValue)}
          minDateAvailable={getMinDateAvailable()}
          maxDateAvailable={getMaxDateAvailable()}
          minDateDeadline={getMinDateDeadline()}
          maxDateDeadline={getMaxDateDeadline()}
        />
        {/* @TODO:
        <FormControl fullWidth>
          <InputLabel id='status'>Module status</InputLabel>
          <Select
            labelId='status'
            value={_status}
            label='Module status'
            onChange={(e) => setStatus(e.target.value)}
          >
            <MenuItem value='draft'>Draft</MenuItem>{/* @TODO: only an option if new goal? /}
            <MenuItem value='published'>Published</MenuItem>
            {/* @TODO:
            <MenuItem value='concluded'>Concluded</MenuItem>{/* @TODO: option to autoconcluded after due date /}
            /}
          </Select>
        </FormControl>
        <p>TODO: duplicate module (save to this goal or another goal)</p>
        */}

        {(props.isAdmin && props.isNew) &&
          <FormControl
            addPadding={true}
            label='Admin options'
            style={styles.adminSettingsWrapper}
          >
            <View style={styles.adminOptionsInner}>
              <TextInput
                value={_pipeDelimitedText}
                mode='outlined'
                onChangeText={(text) => setPipeDelimitedText(text)}
                label='Pipe-delimited module names'
                placeholder={`e.g. "Module name 1|Module name 2|Module name 3"`}
              />

              <Button
                onPress={async () => {
                  const sleep = (ms) => {
                    return new Promise((resolve) => setTimeout(resolve, ms));
                  };
                  const moduleNames = _pipeDelimitedText.split('|');
                  for (const moduleName of moduleNames) {
                    dispatch(createModule(props.goal.classroom_goal_metadata.classroom_id, props.goal.id, moduleName, _dateAvailable, _dateDeadline));
                    await sleep(100); // @TODO: shouldn't need this // @TODO: this still isn't long enough for some of the race conditions
                  }
                  // @TODO: show success/failure (and clear text input on success)
                }}
              >
                Save
              </Button>
            </View>
          </FormControl>
        }
      </Dialog.Content>
      <Dialog.Actions>
        {!props.isNew && props.goal.classroom_goal_metadata.status !== 'published' &&
          <Button
            theme={styles.colors.alert}
            mode='text'
            onPress={() => {
              // @TODO: confirm with alert after letting teacher know how permanent this is...show how many kids have started (maybe not an option if progress has been made)
              if (window.confirm(`Are you sure you want to delete this module?${props.module.assignments.length ? '  All assignments in this module will be deleted as well.' : ''}`)) { // @TODO: don't need to mention about sub assignments if there are none // @TODO: replace with prettier confirm
                dispatch(deleteModule(props.goal.classroom_goal_metadata.classroom_id, props.goal.id, props.module.id));
              }
              props.onClose();
            }}
          >
            {i18n('delete')}
          </Button>
        }
        <Button
          mode='text'
          onPress={() => props.onClose()}
        >
          {i18n('cancel')}
        </Button>
        {/* @TODO: button to duplicate module */}
        <Button
          onPress={() => {
            // @TODO: implement back-end call
            if (props.isNew) {
              // @TODO: make sure all required fields are filled (name not required if admin)
              dispatch(createModule(props.goal.classroom_goal_metadata.classroom_id, props.goal.id, _moduleName, _dateAvailable, _dateDeadline));
              // @TODO: save new module to goal.module_ids
            } else {
              dispatch(updateModule(props.goal.classroom_goal_metadata.classroom_id, props.goal.id, props.module.id, _moduleName, _dateAvailable, _dateDeadline, 'published', undefined)); // @TODO: this should be only fields that have been updated (compare state variables with props.goal) // @TODO: is it okay for the last arg (assignmentIds) to be undefined?
            }
            props.onClose(); // @TODO: wait for back-end call to complete?
          }}
        >
          {i18n('save')}
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
};

class EditModuleDialogProps {
  onClose: () => void;
  goal: any;

  isNew?: boolean;
  module?: any; // @TODO: required if !isNew
  isAdmin?: boolean;
};

export default EditModuleDialog;
